import { Button, MenuItem, Tabs } from '@blueprintjs/core';
import { Select2 } from '@blueprintjs/select';
import React, { useEffect, useState } from 'react';

import { useAppDispatch, useAppSelector } from 'hooks';

import * as store from 'store/scoring';
import { capitalize } from 'utils';
import { RouteCategory, ROUTE_CATEGORIES } from '../../constants';
import Avatar from './Avatar/Avatar';
import BoulderRouteScorer, { LeadRouteScorer } from './RouteScorer/RouteScorer';
import RouteSelector from './RouteSelector';
import styles from './Scoring.module.scss';

export default function Scoring() {
  const selectedCategory = useAppSelector(
    ({ scoring }) => scoring.selectedRouteCategory
  );
  const routesCount =
    useAppSelector(
      ({ scoring }) => scoring.selectedRoutes[selectedCategory]?.length
    ) || 1;

  const maxRoutes = selectedCategory === 'boulder' ? 2 : 1;

  return (
    <>
      <RouteCategorySelector />
      <div className={styles.scoring}>
        {Array(routesCount)
          .fill(0)
          .map((_, idx) => (
            <RouteSection key={idx} routeIndex={idx} canRemove={idx > 0} />
          ))}

        {routesCount < maxRoutes && <AddRouteButton />}
      </div>
    </>
  );
}

function AddRouteButton() {
  const dispatch = useAppDispatch();

  const selectedCategory = useAppSelector(
    ({ scoring }) => scoring.selectedRouteCategory
  );
  const routesCount =
    useAppSelector(
      ({ scoring }) => scoring.selectedRoutes[selectedCategory]?.length
    ) || 1;

  return (
    <Button
      className={styles.addRouteButton}
      icon="plus"
      onClick={() =>
        dispatch(store.selectRoute({ route: null, routeIndex: routesCount }))
      }
    />
  );
}

function RouteCategorySelector() {
  const selectedCategory = useAppSelector(
    (state) => state.scoring.selectedRouteCategory
  );
  const dispatch = useAppDispatch();

  return (
    <div className={styles.routeCategorySelector}>
      <Tabs
        selectedTabId={selectedCategory}
        onChange={(category) =>
          dispatch(store.selectRouteCategory(category as RouteCategory))
        }
        large
        className={styles.routeCategory}
      >
        {ROUTE_CATEGORIES.map((routeCategory) => (
          <Tabs.Tab
            id={routeCategory}
            key={routeCategory}
            className={styles.routeCategory}
          >
            {capitalize(routeCategory)}
          </Tabs.Tab>
        ))}
      </Tabs>
    </div>
  );
}

function RouteSection({
  routeIndex,
  canRemove,
}: {
  routeIndex: number;
  canRemove: boolean;
}) {
  const selectedCategory = useAppSelector(
    (state) => state.scoring.selectedRouteCategory
  );
  const selectedRoute = useAppSelector(
    (state) => state.scoring.selectedRoutes[selectedCategory]?.[routeIndex]
  );

  return (
    <div className={styles.routeSection}>
      <RouteSelector canRemove={canRemove} routeIndex={routeIndex} />

      {selectedRoute && (
        <>
          <ClimberSelector route={selectedRoute} />
          <ClimbersTable route={selectedRoute} routeIndex={routeIndex} />
        </>
      )}
    </div>
  );
}

function ClimbersTable({ route }: { route: number; routeIndex: number }) {
  const selectedCategory = useAppSelector(
    (state) => state.scoring.selectedRouteCategory
  );
  const selectedClimbers = useAppSelector(
    (state) => state.scoring.selectedClimbers[selectedCategory]?.[route] || []
  );

  const climbersData = useAppSelector((state) => state.scoring.allClimbers);

  return (
    <div>
      {selectedClimbers.map((climberId, index) => {
        const climberData = climbersData.find((c) => c.id === climberId);

        if (!climberData) {
          return null;
        }

        return (
          <ClimberRow
            key={climberId}
            route={route}
            climber={climberData}
            active={index === 0}
          />
        );
      })}
    </div>
  );
}

function ClimberRow({
  climber,
  route,
  active,
}: {
  climber: Climber;
  route: number;
  active: boolean;
}) {
  const routeCategory = useAppSelector(
    (state) => state.scoring.selectedRouteCategory
  );

  const dispatch = useAppDispatch();

  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (active) {
      reloadScore();
    }
  }, [active, route, climber.number, routeCategory]);

  const reloadScore = async () => {
    setLoading(true);

    await dispatch(
      store.fetchScore({
        climberId: climber.id,
        route,
        routeCategory,
      })
    );

    setLoading(false);
  };

  const updateScoreForCategory = (routeCategory: RouteCategory) => {
    return async (newScore: RouteScore) => {
      setLoading(true);

      await dispatch(
        store.updateScore({
          score: newScore,
          routeCategory,
        })
      );

      setLoading(false);
    };
  };

  return (
    <div
      onClick={() => {
        if (!active && routeCategory === 'boulder') {
          dispatch(store.addClimberToRoute({ climberId: climber.id, route }));
        }
      }}
      className={styles.climberRow}
    >
      <Avatar climber={climber} />
      {active && (
        <>
          {routeCategory === 'boulder' && (
            <BoulderRouteScorer
              route={route}
              climber={climber}
              loading={loading}
              onScoreUpdate={updateScoreForCategory('boulder')}
            />
          )}
          {routeCategory === 'lead' && (
            <LeadRouteScorer
              route={route}
              climber={climber}
              loading={loading}
              onScoreUpdate={updateScoreForCategory('lead')}
            />
          )}
        </>
      )}
    </div>
  );
}

function ClimberSelector({ route }: { route: number }) {
  const climbers = useAppSelector((state) => state.scoring.allClimbers);
  const routeCategory = useAppSelector(
    (state) => state.scoring.selectedRouteCategory
  );
  const routesPerCategory = useAppSelector(
    (state) => state.scoring.routesPerCategory?.[routeCategory]
  );

  const [eligibleClimbers, setEligibleClimbers] = useState<Climber[]>([]);

  useEffect(() => {
    if (routesPerCategory) {
      const permittedCategories = Object.entries(routesPerCategory).reduce<
        string[]
      >((acc, [category, routes]) => {
        if (routes.includes(route)) {
          acc.push(category);
        }
        return acc;
      }, []);

      setEligibleClimbers(
        climbers.filter((c) => permittedCategories.includes(c.category))
      );
    }
  }, [route, routesPerCategory]);

  const dispatch = useAppDispatch();

  return (
    <div className={styles.climberSelector}>
      <Select2
        inputProps={{ className: styles.input }}
        itemPredicate={(query, climber) =>
          String(climber.number).includes(query) ||
          climber.name.toLowerCase().includes(query.toLowerCase())
        }
        items={eligibleClimbers}
        onItemSelect={() => {}}
        itemRenderer={(climber) => (
          <MenuItem
            key={climber.number}
            text={`${climber.number} - ${climber.name}`}
            onClick={() =>
              dispatch(
                store.addClimberToRoute({ climberId: climber.id, route })
              )
            }
          />
        )}
      >
        <Button
          outlined
          className={styles.newClimberButton}
          text={'New climber'}
          rightIcon="person"
        />
      </Select2>
    </div>
  );
}
